/* eslint-disable camelcase */
import type { GetServerSidePropsContext } from 'next'
import { isNil } from 'ramda'

import type {
  Article,
  CategoryListResponseDataItemCustom,
  GlobalResponse,
  NavigationResponse,
  SharedSeoComponent,
  StaticTextsConfig,
} from '@twisto/api-cms'

import { Seo } from 'homepage/components/atoms'
import { Navigation } from 'homepage/components/navigation'
import { AdditionalDataProvider } from 'homepage/libs/context'
import {
  getArticle,
  getCategory,
  getNavigation,
  getStaticTexts,
} from 'homepage/utils/api'
import { ArticleView } from 'homepage/views'

type ServerSideProps = {
  category: CategoryListResponseDataItemCustom['attributes']
  navigation?: NavigationResponse
  seo?: SharedSeoComponent
  article?: Article
  pageContext: { path?: string | string[] }
  staticTexts: StaticTextsConfig
}

export type DynamicPageProps = ServerSideProps & {
  global: GlobalResponse | null
}

const DynamicPage = ({
  article,
  category,
  seo,
  global,
  navigation,
  staticTexts,
}: DynamicPageProps) => {
  const metadataWithDefaults = {
    ...global?.data?.seo,
    ...seo,
  }
  const navigationData = navigation?.data

  if (isNil(article)) {
    return {
      notFound: true,
    }
  }

  let structuredData

  if (!isNil(article.shortAnswer)) {
    structuredData = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [
        {
          '@type': 'Question',
          name: article.headline,
          acceptedAnswer: {
            '@type': 'Answer',
            text: article.shortAnswer,
          },
        },
      ],
    }
  }

  return (
    <AdditionalDataProvider
      additionalData={{
        eshopsData: { eshops: [], eshop_categories: [] },
        cashbackData: { benefits: [], affiliate_benefits: [] },
        mediaArticlesData: null,
        staticTexts,
      }}
    >
      <Seo {...metadataWithDefaults} structuredData={structuredData} />
      {!isNil(navigationData) && <Navigation {...navigationData} />}

      <ArticleView article={article} category={category} />
    </AdditionalDataProvider>
  )
}

export const getServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const { params } = context

  const url = [params?.path ?? ''].flat().join('/')
  const articleData = await getArticle(`/article/${url}`)

  if (isNil(articleData.data)) {
    return {
      notFound: true,
    }
  }

  const categoryData = await getCategory(articleData.data.category?.path ?? '')
  const staticTexts = await getStaticTexts()

  if (isNil(categoryData.data)) {
    return {
      notFound: true,
    }
  }

  const navigationId = categoryData.data.navigation?.id ?? null
  const navigation = !isNil(navigationId)
    ? await getNavigation(navigationId)
    : undefined

  return {
    props: {
      navigation: navigation ?? null,
      category: categoryData.data,
      article: articleData.data,
      staticTexts: staticTexts.data,
      pageContext: {
        path: articleData.data.path,
      },
    },
  }
}

export default DynamicPage
